import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { createClinicPhysicianAdminClinicPhysiciansPost } from '../../services/api/sdk.gen';
import type {
  ClinicPhysicianResponse,
  OrderDeliveryPreference,
} from '../../services/api/types.gen';
import { NpiInput } from '../NpiInput';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { EMAIL_REGEX } from '../../utils/emailUtils';
import { validateNpi } from '../../utils/npiValidation';
import {
  ORDER_DELIVERY_LABELS,
  ORDER_DELIVERY_VALUES,
} from '../../utils/orderDeliveryPreference';

interface AddPhysicianDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  onCreated: (physician: ClinicPhysicianResponse) => void;
}

/** The contact detail a delivery method needs before it can reach anyone. */
interface DeliveryDestination {
  /** Physician column the entered value is saved to. */
  field: 'fax' | 'email' | 'address';
  label: string;
  inputId: string;
  placeholder?: string;
  type?: string;
  missingMessage: string;
}

/**
 * Delivery methods that imply a destination, and the one they imply.
 *
 * The methods left out — Other, Circle, None — are handled off-system or not
 * at all, so there is nothing to collect for them.
 */
const DELIVERY_DESTINATIONS: Partial<
  Record<OrderDeliveryPreference, DeliveryDestination>
> = {
  fax: {
    field: 'fax',
    label: 'Fax number',
    inputId: 'add-physician-fax',
    missingMessage: 'A fax number is required to send orders by fax.',
  },
  email: {
    field: 'email',
    label: 'Email',
    inputId: 'add-physician-email',
    type: 'email',
    placeholder: 'physician@example.com',
    missingMessage: 'An email address is required to send orders by email.',
  },
  mail: {
    field: 'address',
    label: 'Address',
    inputId: 'add-physician-address',
    missingMessage: 'An address is required to send orders by mail.',
  },
  courier: {
    field: 'address',
    label: 'Address',
    inputId: 'add-physician-address',
    missingMessage: 'An address is required to send orders by courier.',
  },
};

/**
 * Minimal create-physician form for the attending physician picker.
 *
 * Only the fields the picker needs to offer a new option are asked for, plus
 * how this physician receives orders: a physician added here is immediately
 * selectable for future orders, so the delivery method is chosen rather than
 * assumed, and the destination it implies is collected with it. The rest of
 * the record (phone, PECOS, comment) stays an Admin concern.
 *
 * Mount this only while it is open: the form state is the component's own, so
 * a fresh mount is what clears a previous attempt.
 */
export function AddPhysicianDialog({
  open,
  onOpenChange,
  clinicId,
  onCreated,
}: AddPhysicianDialogProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [npi, setNpi] = useState('');
  const [deliveryPreference, setDeliveryPreference] = useState<
    OrderDeliveryPreference | ''
  >('');
  const [destination, setDestination] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const destinationSpec = deliveryPreference
    ? DELIVERY_DESTINATIONS[deliveryPreference]
    : undefined;

  const handleDeliveryPreferenceChange = (value: OrderDeliveryPreference) => {
    setDeliveryPreference(value);
    // A destination typed for the previous method is the wrong place for this
    // one — drop it rather than saving it against the new method.
    setDestination('');
  };

  const handleSave = async () => {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedFirst || !trimmedLast) {
      setFormError('First and last name are required.');
      return;
    }
    if (!npi.trim()) {
      setFormError('NPI is required.');
      return;
    }
    const npiError = validateNpi(npi);
    if (npiError) {
      setFormError(npiError);
      return;
    }
    if (!deliveryPreference) {
      setFormError('Select how this physician receives orders.');
      return;
    }
    const trimmedDestination = destination.trim();
    if (destinationSpec) {
      if (!trimmedDestination) {
        setFormError(destinationSpec.missingMessage);
        return;
      }
      if (
        destinationSpec.field === 'email' &&
        !EMAIL_REGEX.test(trimmedDestination)
      ) {
        setFormError('Enter a valid email address.');
        return;
      }
    }

    setFormError(null);
    setIsSaving(true);
    try {
      const response = await createClinicPhysicianAdminClinicPhysiciansPost({
        body: {
          clinic_id: clinicId,
          first_name: trimmedFirst,
          last_name: trimmedLast,
          suffix: suffix.trim() || null,
          npi: npi.trim(),
          order_delivery_preference: deliveryPreference,
          fax: destinationSpec?.field === 'fax' ? trimmedDestination : null,
          email: destinationSpec?.field === 'email' ? trimmedDestination : null,
          address:
            destinationSpec?.field === 'address' ? trimmedDestination : null,
        },
        throwOnError: true,
      });
      onCreated(response.data);
      onOpenChange(false);
    } catch (e) {
      setFormError(
        getBusinessErrorMessage(e) ?? 'Could not add the physician. Try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[440px]'>
        <DialogHeader>
          <DialogTitle>Add physician</DialogTitle>
          <DialogDescription>
            Adds a physician to this clinic and selects them as the attending
            physician.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          <div className='grid grid-cols-2 gap-3'>
            <div className='space-y-1.5'>
              <Label htmlFor='add-physician-first-name'>First name</Label>
              <Input
                id='add-physician-first-name'
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='add-physician-last-name'>Last name</Label>
              <Input
                id='add-physician-last-name'
                value={lastName}
                onChange={e => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='add-physician-suffix'>Suffix</Label>
            <Input
              id='add-physician-suffix'
              value={suffix}
              placeholder='MD, DO, NP'
              onChange={e => setSuffix(e.target.value)}
            />
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='add-physician-npi'>NPI</Label>
            <NpiInput
              id='add-physician-npi'
              value={npi}
              onValueChange={setNpi}
            />
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='add-physician-delivery-preference'>
              Order delivery preference
            </Label>
            <Select
              value={deliveryPreference}
              onValueChange={handleDeliveryPreferenceChange}
            >
              <SelectTrigger
                id='add-physician-delivery-preference'
                selectSize='default'
              >
                <SelectValue placeholder='Select how orders are sent' />
              </SelectTrigger>
              <SelectContent>
                {ORDER_DELIVERY_VALUES.map(value => (
                  <SelectItem key={value} value={value}>
                    {ORDER_DELIVERY_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {destinationSpec && (
            <div className='space-y-1.5'>
              <Label htmlFor={destinationSpec.inputId}>
                {destinationSpec.label}
              </Label>
              <Input
                id={destinationSpec.inputId}
                type={destinationSpec.type}
                placeholder={destinationSpec.placeholder}
                value={destination}
                onChange={e => setDestination(e.target.value)}
              />
            </div>
          )}

          {formError && (
            <p className='text-xs text-destructive' role='alert'>
              {formError}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type='button' onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Adding...' : 'Add physician'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
